import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  EmptyState,
  PageHeader,
  Skeleton,
  StatusBadge,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatDateInTz, formatCurrency } from "@/design-system/lib/format";
import { PortalChrome } from "../components/PortalChrome";
import type { PortalTabId } from "../components/PortalTabMatrix";
import { usePortalAuth } from "../PortalAuthContext";
import { dashboardPathFor } from "./RecipientDashboardPage";
import { PortalApiError } from "../api/client";
import {
  brokeragePortalApi,
  type BrokeragePortalDeal,
  type BrokerageSubBrokerDetail,
  type PagedResult,
} from "../api/brokerage";
import { StatTile } from "../components/BrokerageBits";

// Brokerage / Sub-Broker — the firm-scoped DETAIL view for one sub-broker, opened
// from a Roster row (/portal/brokerage/sub-brokers/:subBrokerId). It shows what the
// FIRM is entitled to see: identity + affiliation status + gross-only production +
// the sub-broker's own deals — and hosts the single interactive commission-visibility
// control (junior-only; the roster shows a read-only badge). Everything is
// firm-scoped + fail-closed server-side: an off-roster id 404s. GROSS-ONLY — no
// net / cut / owed anywhere. A dedicated sub-route (not a rail tab), rendered in the
// same PortalChrome as the dashboard with the Sub-brokers rail entry highlighted.

const STATUS_TONE: Record<string, StatusTone> = {
  Active: "ok",
  Proposed: "warn",
  Declined: "danger",
  Ended: "muted",
};

const DEALS_PAGE_SIZE = 10;

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

type LoadState = "loading" | "ready" | "notfound" | "error";

export function BrokerageSubBrokerDetailPage() {
  const auth = usePortalAuth();
  const navigate = useNavigate();
  const { subBrokerId } = useParams<{ subBrokerId: string }>();
  const toast = useToast();

  const [detail, setDetail] = useState<BrokerageSubBrokerDetail | null>(null);
  const [loadState, setLoadState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [savingVisibility, setSavingVisibility] = useState(false);

  const [deals, setDeals] = useState<PagedResult<BrokeragePortalDeal> | null>(null);
  const [dealsPage, setDealsPage] = useState(1);

  const loadDetail = useCallback(async () => {
    if (!subBrokerId) {
      setLoadState("notfound");
      return;
    }
    try {
      const d = await brokeragePortalApi.subBroker(subBrokerId);
      setDetail(d);
      setLoadState("ready");
    } catch (err) {
      // Fail-closed server scope — an off-roster id 404s. Everything else is a
      // genuine error, kept distinct from "not on your roster".
      if (err instanceof PortalApiError && err.status === 404) {
        setLoadState("notfound");
        return;
      }
      setLoadError(errorMessage(err, "Couldn't load this sub-broker."));
      setLoadState("error");
    }
  }, [subBrokerId]);

  useEffect(() => {
    let cancelled = false;
    setLoadState("loading");
    void (async () => {
      if (!cancelled) await loadDetail();
    })();
    return () => {
      cancelled = true;
    };
  }, [loadDetail]);

  // The sub-broker's own deals — reuses the roster-scoped ?subBrokerId= drill-down
  // (server-checked against the active roster; a non-active sub-broker resolves an
  // empty page). Only fetched once the detail resolves.
  useEffect(() => {
    if (loadState !== "ready" || !subBrokerId) return;
    let cancelled = false;
    void (async () => {
      try {
        const d = await brokeragePortalApi.dealsList({
          subBrokerId,
          page: dealsPage,
          pageSize: DEALS_PAGE_SIZE,
        });
        if (!cancelled) setDeals(d);
      } catch {
        // Non-fatal — the deals list is secondary to the detail.
        if (!cancelled) setDeals(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadState, subBrokerId, dealsPage]);

  async function handleSetVisibility(showGross: boolean) {
    if (!subBrokerId) return;
    setSavingVisibility(true);
    try {
      await brokeragePortalApi.setVisibility(subBrokerId, showGross);
      await loadDetail();
      toast.success(
        showGross
          ? "Commission gross is now visible to this sub-broker."
          : "Commission figures are now hidden.",
      );
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't update visibility. Try again."));
    } finally {
      setSavingVisibility(false);
    }
  }

  const onSelectTab = (next: PortalTabId) => navigate(`/portal/brokerage?tab=${next}`);

  // Guards — mirror the dashboard page (a non-Brokerage account is bounced home).
  if (!auth.loading && !auth.me) return <Navigate to="/portal" replace />;
  if (auth.me && auth.me.accountType !== "Brokerage") {
    return <Navigate to={dashboardPathFor(auth.me.accountType, auth.me.kind)} replace />;
  }

  return (
    <PortalChrome pageTitle="Brokerage portal" activeTab="roster" onSelectTab={onSelectTab}>
      <div className="space-y-space-5">
        <PageHeader
          eyebrow="Brokerage portal"
          title={detail?.subBrokerName ?? "Sub-broker"}
          breadcrumbs={[
            { label: "Sub-brokers", to: "/portal/brokerage?tab=roster" },
            { label: detail?.subBrokerName ?? "Sub-broker" },
          ]}
        />

        {loadState === "loading" && <Skeleton className="h-40 w-full" />}

        {loadState === "notfound" && (
          <EmptyState
            title="Sub-broker not found"
            description="This sub-broker isn't on your firm's roster."
          />
        )}

        {loadState === "error" && (
          <p className="text-small text-status-error" role="alert">
            {loadError}
          </p>
        )}

        {loadState === "ready" && detail && (
          <div className="space-y-space-5">
            {/* Identity + affiliation — what the firm is entitled to see. */}
            <Card>
              <CardContent className="space-y-space-3">
                <div className="flex flex-wrap items-center gap-space-2">
                  {detail.brokerType === "Junior" ? (
                    <StatusBadge tone="progress">Junior</StatusBadge>
                  ) : (
                    <StatusBadge tone="neutral">Independent</StatusBadge>
                  )}
                  <StatusBadge tone={STATUS_TONE[detail.status] ?? "neutral"}>
                    {detail.status}
                  </StatusBadge>
                  {detail.isPrimary && <StatusBadge tone="muted">Primary firm</StatusBadge>}
                </div>
                <dl className="grid gap-space-3 sm:grid-cols-2">
                  <div>
                    <dt className="text-caption uppercase tracking-wide text-gray-mid">Joined</dt>
                    <dd className="text-small text-onyx">{formatDateInTz(detail.joinedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-caption uppercase tracking-wide text-gray-mid">Accepted</dt>
                    <dd className="text-small text-onyx">
                      {detail.acceptedAt ? formatDateInTz(detail.acceptedAt) : "—"}
                    </dd>
                  </div>
                </dl>
              </CardContent>
            </Card>

            {/* Production — gross-only (no net / cut / owed). */}
            <section className="space-y-space-2">
              <h3 className="m-0 text-small font-semibold text-onyx">Production</h3>
              <div className="grid gap-space-3 sm:grid-cols-2">
                <StatTile label="Deals sourced">{detail.dealCount}</StatTile>
                <StatTile label="Gross">{formatCurrency(detail.ledgerGross)}</StatTile>
              </div>
              {detail.anyUnallocated && (
                <p className="text-caption text-gray-mid">
                  Some commissions are recorded before a split was configured (unallocated).
                </p>
              )}
            </section>

            {/* Commission visibility — junior-only. An independent sub-broker is paid
                directly and sees their own commissions, so the control is omitted. */}
            {detail.brokerType === "Junior" && (
              <Card>
                <CardContent className="space-y-space-2">
                  <h3 className="m-0 text-small font-semibold text-onyx">Commission visibility</h3>
                  <p className="text-caption text-gray-mid">
                    Let this broker see the gross income their deals generate, in their own portal.
                  </p>
                  <select
                    aria-label="Commission visibility"
                    value={detail.financialVisibility === "None" ? "hide" : "show"}
                    onChange={(ev) => void handleSetVisibility(ev.target.value === "show")}
                    disabled={detail.status !== "Active" || savingVisibility}
                    className="h-10 px-space-3 py-[10px] font-body text-small text-onyx bg-white-pure border border-border-light rounded-md transition-colors duration-fast ease-standard focus:outline-none focus:border-gold focus:shadow-goldRing disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed sm:max-w-xs"
                  >
                    <option value="show">Show gross</option>
                    <option value="hide">Hide</option>
                  </select>
                  {detail.status !== "Active" && (
                    <p className="text-caption text-gray-mid">
                      Available once the affiliation is active.
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* The sub-broker's own deals (roster-scoped drill-down). */}
            <section className="space-y-space-2">
              <h3 className="m-0 text-small font-semibold text-onyx">Deals</h3>
              {!deals ? (
                <Skeleton className="h-32 w-full" />
              ) : deals.data.length === 0 ? (
                <EmptyState
                  title="No deals yet"
                  description="This sub-broker hasn't sourced any deals with your firm."
                />
              ) : (
                <>
                  <div className="overflow-x-auto rounded-lg border border-border-light">
                    <table className="w-full text-small">
                      <thead>
                        <tr className="border-b border-border-light bg-white-warm text-left text-caption uppercase tracking-wide text-gray-mid">
                          <th className="px-space-3 py-space-2 font-medium">Deal #</th>
                          <th className="px-space-3 py-space-2 font-medium">Business</th>
                          <th className="px-space-3 py-space-2 text-right font-medium">
                            Purchase price
                          </th>
                          <th className="px-space-3 py-space-2 font-medium">Status</th>
                          <th className="px-space-3 py-space-2 font-medium">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {deals.data.map((d) => (
                          <tr key={d.id} className="border-b border-border-light last:border-0">
                            <td className="px-space-3 py-space-2 text-onyx">{d.dealNumber}</td>
                            <td className="px-space-3 py-space-2 text-gray-mid">
                              {d.businessName ?? "—"}
                            </td>
                            <td className="px-space-3 py-space-2 text-right text-onyx">
                              {formatCurrency(d.purchasePrice)}
                            </td>
                            <td className="px-space-3 py-space-2 text-gray-mid">{d.status}</td>
                            <td className="px-space-3 py-space-2 text-gray-mid">
                              {formatDateInTz(d.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {deals.totalCount > deals.pageSize && (
                    <div className="flex items-center justify-between">
                      <span className="text-caption text-gray-mid">
                        Page {deals.page} of{" "}
                        {Math.max(1, Math.ceil(deals.totalCount / deals.pageSize))} · {deals.totalCount}{" "}
                        total
                      </span>
                      <div className="flex gap-space-2">
                        <button
                          type="button"
                          disabled={dealsPage <= 1}
                          onClick={() => setDealsPage((p) => Math.max(1, p - 1))}
                          className="rounded-lg border border-border-light px-space-3 py-space-1 text-small font-medium text-onyx enabled:hover:border-gold disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          disabled={
                            dealsPage >= Math.max(1, Math.ceil(deals.totalCount / deals.pageSize))
                          }
                          onClick={() => setDealsPage((p) => p + 1)}
                          className="rounded-lg border border-border-light px-space-3 py-space-1 text-small font-medium text-onyx enabled:hover:border-gold disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        )}
      </div>
    </PortalChrome>
  );
}
